import { FormCheckbox } from '@/components/forms/form-checkbox.js';
import { FormInputField } from '@/components/forms/form-input-field.js';
import { Button } from '@/components/ui/button.js';
import { Form } from '@/components/ui/form.js';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check } from 'lucide-react';
import { ReactNode, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

interface PurgeConfirmationGateProps {
  expectedName: string;
  expectedInput?: string;
  label: string;
  action: string;
  checkboxLabel?: string;
  note?: ReactNode;
  disabled?: boolean;
  testId: string;
  onConfirmed: (confirmedName: string) => void;
}

export const PurgeConfirmationGate = ({
  expectedName,
  expectedInput,
  label,
  action,
  checkboxLabel,
  note,
  disabled,
  testId,
  onConfirmed,
}: PurgeConfirmationGateProps) => {
  const [confirmed, setConfirmed] = useState(false);

  const expectedText = (expectedInput ?? expectedName).trim();

  const formSchema = useMemo(
    () =>
      z.object({
        confirmationName: z
          .string()
          .trim()
          .min(1, 'Type the confirmation text to continue.')
          .refine((typed) => typed === expectedText, 'The confirmation text must match exactly.'),
        acknowledged: z.boolean().refine((checked) => checked || !checkboxLabel, 'Tick the box to continue.'),
      }),
    [expectedText, checkboxLabel],
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { confirmationName: '', acknowledged: false },
    mode: 'onChange',
  });

  const onSubmit = form.handleSubmit(({ confirmationName }) => {
    if (disabled) {
      return;
    }

    setConfirmed(true);
    onConfirmed(confirmationName);
  });

  if (confirmed) {
    return (
      <p className="flex items-center gap-1 text-sm text-gray-600" data-testid={`${testId}-confirmed`}>
        <Check className="h-4 w-4" />
        Confirmed as {expectedName.trim()}
      </p>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={onSubmit}>
        {note && (
          <p className="mb-3 text-sm text-gray-600" data-testid={`${testId}-note`}>
            {note}
          </p>
        )}
        {checkboxLabel && (
          <FormCheckbox
            form={form}
            name="acknowledged"
            label={checkboxLabel}
            horizontal
            className="mb-3"
            labelClassName="font-normal text-sm"
            checkboxProps={{ disabled, 'data-testid': `${testId}-acknowledge` }}
          />
        )}
        <div className="mb-4">
          <p className="text-sm font-bold">Confirmation text</p>
          <p className="mt-1 select-all break-words font-mono text-sm text-gray-600" data-testid={`${testId}-expected`}>
            {expectedText}
          </p>
        </div>
        <FormInputField
          form={form}
          name="confirmationName"
          label={label}
          inputProps={{
            placeholder: expectedText,
            autoComplete: 'off',
            disabled,
            'data-testid': `${testId}-name`,
          }}
        />
        <Button
          type="submit"
          variant="destructive"
          className="mt-4"
          disabled={disabled || !form.formState.isValid}
          data-testid={`${testId}-submit`}
        >
          {action}
        </Button>
      </form>
    </Form>
  );
};
